import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const LiveStreamCard = ({ stream }) => {
  // Extract data with fallbacks for different API response formats
  // Live stream data is nested in stream_info when user is live
  const streamInfo = stream.stream_info || {};
  const display_name = stream.team_display_name || stream.display_name || stream.user || stream.user_name || stream.username || 'Unknown Streamer';
  const game_name = streamInfo.game_name || stream.game_name || stream.game || 'Just Chatting';
  const title = streamInfo.title || stream.title || stream.stream_title || 'Untitled Stream';
  const viewer_count = streamInfo.viewer_count || stream.viewer_count || stream.viewers || 0;
  const user_name = stream.user_name || stream.username || stream.twitch || stream.twitch_username || '';
  
  // Use stream thumbnail for live preview, fallback to profile image
  let thumbnail_url = streamInfo.thumbnail_url || stream.thumbnail_url || stream.thumbnail;
  if (thumbnail_url && thumbnail_url.includes('{width}x{height}')) {
    thumbnail_url = thumbnail_url.replace('{width}', '320').replace('{height}', '180');
  }
  
  // Use profile image as fallback
  if (!thumbnail_url) {
    thumbnail_url = stream.userprofileimageurl || stream.profile_image_url;
  }

  const formatViewerCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count?.toString() || '0';
  };

  return (
    <div className="stream-card card" style={{
      height: '400px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Thumbnail */}
      <div className="stream-thumbnail">
        <img
          src={thumbnail_url || 'https://via.placeholder.com/320x180/4a9d4a/ffffff?text=Live+Stream'}
          alt={`${display_name} stream thumbnail`}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/320x180/4a9d4a/ffffff?text=Live+Stream';
          }}
        />
        <div className="live-badge">
          🔴 LIVE
        </div>
        <div className="viewer-count">
          {formatViewerCount(viewer_count)} viewers
        </div>
      </div>

      {/* Stream Info */}
      <div style={{
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '1rem 0'
      }}>
        <div>
          <h3 style={{
            fontWeight: 'bold', 
            color: 'var(--forest-800)', 
            fontSize: '1rem', 
            marginBottom: '0.5rem',
            lineHeight: '1.3',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {title || 'Untitled Stream'}
          </h3>
          <p style={{
            color: 'var(--forest-600)', 
            fontWeight: '500',
            fontSize: '0.875rem',
            marginBottom: '0.5rem'
          }}>
            {display_name}
          </p>
          <p style={{
            color: 'var(--earth-600)', 
            fontSize: '0.75rem',
            marginBottom: '1rem'
          }}>
            Playing: {game_name}
          </p>
        </div>
        
        {/* Watch Now Button - Always at bottom */}
        <a
          href={`https://twitch.tv/${user_name}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary w-full text-center"
          style={{display: 'block', marginTop: 'auto'}}
        >
          Watch Now
        </a>
      </div>
    </div>
  );
};

const LiveStreamCarousel = () => {
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [randomMember, setRandomMember] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);
  const [countdownProgress, setCountdownProgress] = useState(0);

  useEffect(() => {
    fetchLiveStreams();
    
    // Check if mobile on mount and resize
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Set up polling for live stream updates, but only when page is visible
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchLiveStreams();
      }
    }, 120000); // Update every 2 minutes
    
    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, fetch fresh data
        fetchLiveStreams();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Auto-scroll effect for mobile with countdown
  useEffect(() => {
    if (!isMobile || liveStreams.length <= 1 || autoScrollPaused) {
      setCountdownProgress(0);
      return;
    }
    
    let progressInterval;
    let autoScrollTimeout;
    
    const startCountdown = () => {
      setCountdownProgress(0);
      
      // Update progress every 100ms for smooth animation
      progressInterval = setInterval(() => {
        setCountdownProgress(prev => {
          const newProgress = prev + (100 / 10000) * 100; // 100ms / 10000ms * 100%
          return newProgress >= 100 ? 100 : newProgress;
        });
      }, 100);
      
      // Auto-scroll after 10 seconds
      autoScrollTimeout = setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % liveStreams.length);
        setCountdownProgress(0);
      }, 10000);
    };
    
    startCountdown();
    
    return () => {
      clearInterval(progressInterval);
      clearTimeout(autoScrollTimeout);
    };
  }, [isMobile, liveStreams.length, autoScrollPaused, currentIndex]);
  
  // Resume auto-scroll after 30 seconds of inactivity
  useEffect(() => {
    if (!autoScrollPaused) return;
    
    const resumeTimeout = setTimeout(() => {
      setAutoScrollPaused(false);
    }, 30000); // Resume after 30 seconds
    
    return () => clearTimeout(resumeTimeout);
  }, [autoScrollPaused]);

  const fetchLiveStreams = async () => {
    try {
      const response = await apiService.getLiveStreams();
      setLiveStreams(response.data.live_streams || []);
      
      // If no live streams, get a random team member for feature
      if (!response.data.live_streams || response.data.live_streams.length === 0) {
        const teamResponse = await apiService.getTeamMembers();
        const allMembers = [
          ...(teamResponse.data.founder || []),
          ...(teamResponse.data.directors || []),
          ...(teamResponse.data.advisors || []),
          ...(teamResponse.data.members || [])
        ];
        
        if (allMembers.length > 0) {
          const randomIndex = Math.floor(Math.random() * allMembers.length);
          setRandomMember(allMembers[randomIndex]);
        }
      }
      
      setError(null);
    } catch (err) {
      setError('Unable to fetch live streams');
      console.error('Error fetching live streams:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="spinner"></div>
        <p style={{color: 'var(--forest-600)', marginTop: '1rem'}}>Loading live streams...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p style={{color: 'var(--earth-600)', marginBottom: '1rem'}}>⚠️ {error}</p>
        <button
          onClick={fetchLiveStreams}
          className="btn btn-outline"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (liveStreams.length === 0) {
    return (
      <div className="text-center py-12">
        {randomMember ? (
          <div>
            <div style={{marginBottom: '1.5rem'}}>
              <img
                src={randomMember.profile_image_url || `https://via.placeholder.com/120x120/4a9d4a/ffffff?text=${randomMember.display_name?.charAt(0) || 'M'}`}
                alt={randomMember.display_name}
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  margin: '0 auto',
                  border: '4px solid var(--forest-300)',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                }}
              />
            </div>
            <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--forest-800)', marginBottom: '0.5rem'}}>
              Have you checked out {randomMember.display_name || randomMember.twitch_username} before?
            </h3>
            <p style={{color: 'var(--forest-600)', marginBottom: '1.5rem'}}>
              {randomMember.role && `${randomMember.role} • `}
              Check out their content and give them a follow!
            </p>
            <a
              href={`https://twitch.tv/${randomMember.twitch_username || randomMember.twitch}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              Visit {randomMember.display_name || randomMember.twitch_username}'s Channel
            </a>
          </div>
        ) : (
          <div>
            <div style={{marginBottom: '1.5rem', fontSize: '3.75rem'}}>
              🌙
            </div>
            <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--forest-800)', marginBottom: '0.5rem'}}>
              No one's live right now
            </h3>
            <p style={{color: 'var(--forest-600)'}}>
              Check back soon! Our team members love to stream throughout the day.
            </p>
          </div>
        )}
      </div>
    );
  }

  const nextStream = () => {
    setAutoScrollPaused(true);
    setCountdownProgress(0);
    setCurrentIndex(prev => (prev + 1) % liveStreams.length);
  };

  const prevStream = () => {
    setAutoScrollPaused(true);
    setCountdownProgress(0);
    setCurrentIndex(prev => (prev - 1 + liveStreams.length) % liveStreams.length);
  };
  
  // Circular progress SVG component
  const CircularProgress = ({ progress, size = 32 }) => {
    const radius = (size - 4) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset = circumference - (progress / 100) * circumference;
    
    return (
      <svg 
        width={size} 
        height={size} 
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none'
        }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(74, 157, 74, 0.3)"
          strokeWidth="2"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--forest-600)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: 'stroke-dashoffset 0.1s ease-out'
          }}
        />
      </svg>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gradient">
        🔴 Now Live ({liveStreams.length})
      </h2>

      {/* Mobile carousel with navigation arrows */}
      {isMobile && liveStreams.length > 1 ? (
        <div className="mobile-stream-carousel" style={{position: 'relative'}}>
          {/* Navigation Arrows */}
          <button 
            onClick={prevStream}
            className="carousel-arrow carousel-arrow-left"
            style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              color: 'var(--forest-600)',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            ‹
            {!autoScrollPaused && liveStreams.length > 1 && (
              <CircularProgress progress={countdownProgress} size={36} />
            )}
          </button>
          
          <button 
            onClick={nextStream}
            className="carousel-arrow carousel-arrow-right"
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              color: 'var(--forest-600)',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            ›
            {!autoScrollPaused && liveStreams.length > 1 && (
              <CircularProgress progress={countdownProgress} size={36} />
            )}
          </button>

          {/* Single stream display */}
          <div style={{overflow: 'hidden', display: 'flex', justifyContent: 'center'}}>
            <div style={{
              display: 'flex',
              transform: `translateX(-${currentIndex * 100}%)`,
              transition: 'transform 0.3s ease',
              width: '100%',
              maxWidth: '20rem'
            }}>
              {liveStreams.map((stream, index) => (
                <div key={`${stream.user_name}-${index}`} style={{minWidth: '100%', display: 'flex', justifyContent: 'center'}}>
                  <LiveStreamCard stream={stream} />
                </div>
              ))}
            </div>
          </div>

          {/* Dot indicators */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '16px'
          }}>
            {liveStreams.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: index === currentIndex ? 'var(--forest-600)' : 'var(--forest-200)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        </div>
      ) : (
        /* Desktop infinite looping carousel */
        <div className="desktop-stream-carousel">
          <div 
            className="stream-cards-infinite"
            style={{
              animation: liveStreams.length > 1 ? `infiniteScroll ${liveStreams.length * 8}s linear infinite` : 'none'
            }}
          >
            {/* Duplicate streams for seamless infinite scroll */}
            {liveStreams.length > 1 ? (
              [...liveStreams, ...liveStreams].map((stream, index) => (
                <div key={`${stream.user_name}-${index}-desktop`} style={{flex: 'none', width: '20rem'}}>
                  <LiveStreamCard stream={stream} />
                </div>
              ))
            ) : (
              liveStreams.map((stream, index) => (
                <div key={`${stream.user_name}-${index}-desktop`} style={{flex: 'none', width: '20rem'}}>
                  <LiveStreamCard stream={stream} />
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveStreamCarousel;